import { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { authService } from '@/lib/auth'

interface ProtectedRouteProps {
  children: ReactNode
  resource?: string
  action?: string
  requireApprovedUser?: boolean
}

export function ProtectedRoute({
  children,
  resource,
  action = 'read',
  requireApprovedUser = false
}: ProtectedRouteProps) {
  const location = useLocation()
  const isAuthenticated = authService.isAuthenticated()
  const user = authService.getUser()

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />
  }

  // Force re-login if user session is missing permissions (old JWT)
  if (user && !user.permissions) {
    authService.clearAuth()
    return <Navigate to="/login" replace />
  }

  // Check if user must change password
  if (user?.must_change_password && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />
  }

  // Check if user is new_user (not approved yet)
  const isNewUser = user?.role?.toLowerCase() === 'new_user'
  if (isNewUser && location.pathname !== '/pending-approval' && location.pathname !== '/change-password') {
    return <Navigate to="/pending-approval" replace />
  }

  // Check approved user requirement (not new_user)
  if (requireApprovedUser && isNewUser) {
    return <Navigate to="/pending-approval" replace />
  }

  // Check permission-based access
  if (resource && user?.permissions) {
    const permissions = user.permissions
    // Root wildcard shortcut
    const isRoot = permissions['*']?.includes('*')
    if (!isRoot) {
      const actions = permissions[resource]
      const hasAccess = actions && (actions.includes(action) || actions.includes('*'))
      if (!hasAccess) {
        return <Navigate to="/" replace />
      }
    }
  }

  return <>{children}</>
}
